import axios from '../api/axios';
import useAuth from './useAuth';

// Ensure only one refresh request runs at a time (per page instance)
let refreshPromise = null;

// Cross-tab coordination + jittered backoff to avoid refresh token reuse races
const LOCK_KEY = 'rf_lock_v1';
const LAST_KEY = 'rf_last_v1';

const now = () => Date.now();
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const safeGet = (key) => {
  try { return localStorage.getItem(key); } catch { return null; }
}
const safeSet = (key, val) => {
  try { localStorage.setItem(key, val); } catch {}
}
const safeRemove = (key) => {
  try { localStorage.removeItem(key); } catch {}
}

const tryAcquireLock = (id, ttlMs) => {
  try {
    const raw = localStorage.getItem(LOCK_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.ts === 'number' && parsed.ts + ttlMs > now()) {
          return false; // lock still valid
        }
      } catch {}
    }
    localStorage.setItem(LOCK_KEY, JSON.stringify({ id, ts: now() }));
    return true;
  } catch {
    // If localStorage is unavailable, just pretend we got the lock
    return true;
  }
};

const releaseLock = (id) => {
  try {
    const raw = localStorage.getItem(LOCK_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.id === id) {
      localStorage.removeItem(LOCK_KEY);
    }
  } catch {}
};

const useRefreshToken = () => {
  const { setAuth } = useAuth();

  const refresh = async () => {
    if (refreshPromise) {
      return refreshPromise;
    }

    const applyAuthFromResponse = (response) => {
      setAuth((prev) => ({
        ...prev,
        accessToken: response.data.accessToken,
        user: response.data.email,
        role: response.data.role,
        uuid: response.data.uuid,
        avatar: response.data.avatar,
        id: response.data.id,
      }));
      safeSet(LAST_KEY, String(now()));
      return response.data.accessToken;
    };

    refreshPromise = (async () => {
      const lockId = `${now()}_${Math.random().toString(36).slice(2)}`;
      const lockTtlMs = 3500;
      const maxWaitMs = 4000;
      const startWait = now();
      // Wait for an existing refresh in another tab/reload to complete
      while (!tryAcquireLock(lockId, lockTtlMs)) {
        if (now() - startWait > maxWaitMs) break; // don't wait forever
        await sleep(150);
      }

      // If a very recent refresh happened, delay slightly so Set-Cookie settles
      const lastStr = safeGet(LAST_KEY);
      const last = lastStr ? parseInt(lastStr, 10) : 0;
      const since = last ? now() - last : Infinity;
      if (since < 500) {
        await sleep(500 - since);
      }

      try {
        // Try once; only retry quickly on 403 (rotation race). Never retry 401 (no cookie).
        try {
          const response = await axios.get('/refresh', { withCredentials: true });
          return applyAuthFromResponse(response);
        } catch (err) {
          const status = err?.response?.status;
          // If unauthorized, bail fast (likely no refresh cookie).
          if (status === 401) throw err;
          // If forbidden, retry once after a brief backoff (possible rotation race).
          if (status === 403) {
            await sleep(250);
            const retry = await axios.get('/refresh', { withCredentials: true });
            return applyAuthFromResponse(retry);
          }
          // Non-auth errors: propagate
          throw err;
        }
      } finally {
        releaseLock(lockId);
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  };
  return refresh;
};

export default useRefreshToken;
